import ReactEcharts from 'echarts-for-react'
import { useEffect, useState } from 'react'

import { ContentCard, Leaderboard } from '@/components/customComponents'

import {
  lineChartOptions,
  pieChartOptions,
  waterfallChartOptions,
} from '@/constants/CHART_CONSTANTS'
import {
  MOCK_SALES_ITEM_LEADERBOARD_DATA_1,
  MOCK_STORE_LEADERBOARD_DATA_1,
  MOCK_THEORETICAL_VS_ACTUAL_DATA_1,
  MOCK_VARIANCE_ATTRIBUTION_DATA_1,
  MOCK_WATERFALL_DATA_1,
} from '@/screens/clarityLens/spendLens/CONSTANTS'

interface TheoreticalVsActualTabProps {
  itemName: string
}

export const TheoreticalVsActualTab = ({
  itemName: itemName,
}: TheoreticalVsActualTabProps) => {
  const [waterfallData, setWaterfallData] =
    useState<typeof MOCK_WATERFALL_DATA_1>()
  const [theoreticalVsActualData, setTheoreticalVsActualData] =
    useState<typeof MOCK_THEORETICAL_VS_ACTUAL_DATA_1>()
  const [varianceAttributionData, setVarianceAttributionData] =
    useState<typeof MOCK_VARIANCE_ATTRIBUTION_DATA_1>()
  const [storeLeaderboardData, setStoreLeaderboardData] =
    useState<typeof MOCK_STORE_LEADERBOARD_DATA_1>()
  const [salesItemLeaderboardData, setSalesItemLeaderboardData] =
    useState<typeof MOCK_SALES_ITEM_LEADERBOARD_DATA_1>()

  useEffect(() => {
    if (itemName === 'Oat Milk') {
      setWaterfallData(MOCK_WATERFALL_DATA_1)
      setTheoreticalVsActualData(MOCK_THEORETICAL_VS_ACTUAL_DATA_1)
      setVarianceAttributionData(MOCK_VARIANCE_ATTRIBUTION_DATA_1)
      setStoreLeaderboardData(MOCK_STORE_LEADERBOARD_DATA_1)
      setSalesItemLeaderboardData(MOCK_SALES_ITEM_LEADERBOARD_DATA_1)
    } else if (itemName === 'Almond Croissant') {
      setWaterfallData(MOCK_WATERFALL_DATA_1)
      setTheoreticalVsActualData(MOCK_THEORETICAL_VS_ACTUAL_DATA_1)
      setVarianceAttributionData(MOCK_VARIANCE_ATTRIBUTION_DATA_1)
      setStoreLeaderboardData(MOCK_STORE_LEADERBOARD_DATA_1)
      setSalesItemLeaderboardData(MOCK_SALES_ITEM_LEADERBOARD_DATA_1)
    } else {
      setWaterfallData(MOCK_WATERFALL_DATA_1)
      setTheoreticalVsActualData(MOCK_THEORETICAL_VS_ACTUAL_DATA_1)
      setVarianceAttributionData(MOCK_VARIANCE_ATTRIBUTION_DATA_1)
      setStoreLeaderboardData(MOCK_STORE_LEADERBOARD_DATA_1)
      setSalesItemLeaderboardData(MOCK_SALES_ITEM_LEADERBOARD_DATA_1)
    }
  }, [])

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ContentCard
          title="Cost Breakdown Analysis"
          loading={!waterfallData}
          isBeta
        >
          {waterfallData && (
            <ReactEcharts
              option={waterfallChartOptions(
                waterfallData.values,
                waterfallData.labels,
                'money'
              )}
            />
          )}
        </ContentCard>
        <ContentCard
          title="Theoretical vs Actual"
          loading={!theoreticalVsActualData}
          isBeta
        >
          {theoreticalVsActualData && (
            <ReactEcharts option={getLineChartData(theoreticalVsActualData)} />
          )}
        </ContentCard>
        <ContentCard
          title="Variance Attribution"
          loading={!varianceAttributionData}
          isBeta
        >
          {varianceAttributionData && (
            <ReactEcharts
              option={getPieChartData(
                varianceAttributionData.record,
                varianceAttributionData.labels
              )}
            />
          )}
        </ContentCard>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Leaderboard
          title="Store Leaderboard"
          data={storeLeaderboardData}
          columns={[
            {
              header: 'Store Name',
              accessorKey: 'storeName',
              type: 'text',
            },
            {
              header: 'Deviation %',
              accessorKey: 'deviationPercentage',
              type: 'percentage',
            },
            {
              header: 'Deviation $',
              accessorKey: 'deviation',
              type: 'money',
            },
            {
              header: 'Total Units Consumed',
              accessorKey: 'totalUnitsConsumed',
              type: 'number',
            },
          ]}
          loading={!storeLeaderboardData}
          enablePagination
          itemsPerPage={5}
          isBeta
        />
        <Leaderboard
          title="Sales Item Leaderboard"
          data={salesItemLeaderboardData}
          columns={[
            {
              header: 'Sales Item Name',
              accessorKey: 'salesItemName',
              type: 'text',
            },
            {
              header: 'Variance $ Impact',
              accessorKey: 'varianceImpact',
              type: 'money',
            },
            {
              header: 'Units Sold',
              accessorKey: 'unitsSold',
              type: 'number',
            },
          ]}
          loading={!salesItemLeaderboardData}
          enablePagination
          itemsPerPage={5}
          isBeta
        />
      </div>
    </div>
  )
}

const getPieChartData = (dataObj: Record<string, number>, labels: string[]) => {
  const data = labels.map((label) => dataObj[label] ?? 0)
  return pieChartOptions(data, labels, 'percentage')
}

const getLineChartData = (data: typeof MOCK_THEORETICAL_VS_ACTUAL_DATA_1) => {
  // Safety check for empty data
  if (!data || Object.keys(data).length === 0) {
    return lineChartOptions([], [], [], 'money')
  }

  const periods = Object.keys(data) // These will be our line names/series
  const allMonths = new Set<string>()

  // Collect all unique months (these will be our X-axis data points)
  periods.forEach((period) => {
    if (
      data[period as keyof typeof data] &&
      Array.isArray(data[period as keyof typeof data])
    ) {
      data[period as keyof typeof data].forEach((item) => {
        if (item && item.month) {
          allMonths.add(item.month)
        }
      })
    }
  })

  const monthNames = Array.from(allMonths) // Months should be in their natural order

  // Safety check for no months found
  if (monthNames.length === 0) {
    return lineChartOptions([], [], [], 'money')
  }

  // Create raw data for each period (line)
  const rawData = periods.map((period) => {
    return monthNames.map((month) => {
      const monthData = data[period as keyof typeof data]?.find(
        (item: any) => item?.month === month
      )
      return monthData ? monthData.price : 0
    })
  })

  return lineChartOptions(rawData, periods, monthNames, 'money')
}
